import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { can, type Capability } from '@sauda/shared';
import { api, onAuthLost, refreshAccessToken, setAccessToken } from '../lib/api.js';
import type { MeResponse, Membership } from '../lib/types.js';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthValue {
  status: Status;
  userId: string | null;
  /** The signed in person's name, for initials. */
  name: string | null;
  agencyId: string | null;
  memberships: Membership[];
  /** Set while a platform admin is looking in, read only. */
  impersonation: { reason: string } | null;
  /** The membership in the current agency, null before an agency exists. */
  current: Membership | null;
  /** True when the current role has the capability in the shared permission table. */
  allowed: (capability: Capability) => boolean;
  /** Stores a token from sign in, accept or switch, then reloads who the user is. */
  adopt: (accessToken: string) => Promise<MeResponse>;
  signOut: () => Promise<void>;
  /** Drops an admin's read only view of an agency without touching any real sign in. */
  leaveImpersonation: () => void;
  reload: () => Promise<MeResponse | null>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);

  const load = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const loaded = await api<MeResponse>('/me');
      setMe(loaded);
      setStatus('authenticated');
      return loaded;
    } catch {
      setMe(null);
      setStatus('anonymous');
      return null;
    }
  }, []);

  useEffect(() => {
    onAuthLost(() => {
      setAccessToken(null);
      setMe(null);
      setStatus('anonymous');
    });
    let cancelled = false;
    void (async () => {
      const restored = await refreshAccessToken();
      if (cancelled) return;
      if (restored) await load();
      else setStatus('anonymous');
    })();
    return () => {
      cancelled = true;
      onAuthLost(null);
    };
  }, [load]);

  const adopt = useCallback(async (accessToken: string) => {
    setAccessToken(accessToken);
    const loaded = await api<MeResponse>('/me');
    setMe(loaded);
    setStatus('authenticated');
    return loaded;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', auth: false });
    } finally {
      setAccessToken(null);
      setMe(null);
      setStatus('anonymous');
    }
  }, []);

  const leaveImpersonation = useCallback(() => {
    setAccessToken(null);
    setMe(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthValue>(() => {
    const current = me?.memberships.find((m) => m.agencyId === me.agencyId) ?? null;
    return {
      status,
      userId: me?.userId ?? null,
      name: me?.name ?? null,
      agencyId: me?.agencyId ?? null,
      memberships: me?.memberships ?? [],
      impersonation: me?.impersonation ?? null,
      current,
      allowed: (capability) => current !== null && can(current.role, capability),
      adopt,
      signOut,
      leaveImpersonation,
      reload: load,
    };
  }, [status, me, adopt, signOut, leaveImpersonation, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
